import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Save, Building2, User, Mail, Phone, MapPin, BadgeCheck,
  Briefcase, FileText, Globe, Hash
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';

export default function UserProfilePage() {
  const { userProfile, setUserProfile, activeStandard } = useSession();
  const { user, isAuthenticated } = useAuth();

  const [form, setForm] = useState({
    companyName: '',
    fullName: '',
    jobTitle: '',
    email: '',
    phone: '',
    address: '',
    abn: '',
    licenseNumber: '',
    companyRegNumber: '',
    vatNumber: '',
    country: 'GB' as 'GB' | 'AU' | 'US' | 'CA' | 'NZ' | 'IE' | 'DE' | 'FR' | 'SG' | 'OTHER',
  });

  const isUK = form.country === 'GB' || form.country === 'IE';
  const isAU = form.country === 'AU' || form.country === 'NZ';

  useEffect(() => {
    if (userProfile) {
      setForm({
        companyName: userProfile.companyName || '',
        fullName: userProfile.fullName || '',
        jobTitle: userProfile.jobTitle || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        address: userProfile.address || '',
        abn: userProfile.abn || '',
        licenseNumber: userProfile.licenseNumber || '',
        companyRegNumber: userProfile.companyRegNumber || '',
        vatNumber: userProfile.vatNumber || '',
        country: (userProfile.country as any) || (isUK ? 'GB' : isAU ? 'AU' : 'GB'),
      });
    } else if (user?.name) {
      setForm(prev => ({ ...prev, fullName: user.name }));
    }
  }, [userProfile, user]);

  const handleSave = () => {
    setUserProfile({ ...form });
    toast.success('Profile saved successfully');
  };

  const field = (label: string, icon: React.ElementType, key: keyof typeof form, placeholder: string) => (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
        <icon className="w-3.5 h-3.5" />
        {label}
      </label>
      <input
        type="text"
        value={form[key]}
        onChange={(e) => setForm(prev => ({ ...prev, [key]: e.target.value }))}
        placeholder={placeholder}
        className="w-full px-3 py-3 sm:py-2 rounded-lg text-base sm:text-sm text-white placeholder-slate-600 border border-slate-700 focus:border-rb-brand focus:ring-1 focus:ring-rb-brand/20 outline-none transition-all"
        style={{ background: 'var(--bg-input, #0f172a)' }}
      />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        icon={User}
        title="User Profile"
        subtitle="Manage your company details and professional information"
        actions={
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSave}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold bg-rb-brand/10 text-rb-brand border border-rb-brand/20 hover:bg-rb-brand/20 transition-all"
          >
            <Save className="w-3.5 h-3.5" /> Save Profile
          </motion.button>
        }
      />

      {/* Region banner */}
      <div className={`p-3 rounded-xl border flex items-center gap-2 ${
        isUK ? 'bg-blue-500/5 border-blue-500/20' : isAU ? 'bg-amber-500/5 border-amber-500/20' : 'bg-slate-800/30 border-slate-700'
      }`}>
        <Globe className={`w-4 h-4 ${isUK ? 'text-blue-400' : isAU ? 'text-amber-400' : 'text-slate-500'}`} />
        <span className="text-xs">
          <span className="text-slate-500">Profile fields are configured for </span>
          <span className={`font-bold ${isUK ? 'text-blue-400' : isAU ? 'text-amber-400' : 'text-slate-300'}`}>
            {isUK ? '🇬🇧 UK regulations' : isAU ? '🇦🇺 Australian regulations' : 'General'}
          </span>
        </span>
      </div>

      {/* Profile Card */}
      <div className="rb-card p-6 space-y-6">
        {/* Avatar + Name */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-rb-brand/20 to-cyan-500/20 border border-rb-brand/30 flex items-center justify-center">
            <User className="w-8 h-8 text-rb-brand" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">
              {isAuthenticated ? (user?.name || 'User') : 'Guest User'}
            </h3>
            <p className="text-xs text-slate-500">
              {isAuthenticated ? 'Authenticated User' : 'Not logged in — data stored locally'}
            </p>
          </div>
        </div>

        <div className="h-px bg-slate-800" />

        {/* Company Section */}
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-rb-brand flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Company Information
          </h4>
          <p className="text-xs text-slate-500">This appears on reports and the top navigation bar</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Country selector */}
          <div className="md:col-span-2 space-y-1.5">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              Country / Region
            </label>
            <select
              value={form.country}
              onChange={(e) => {
                const newCountry = e.target.value as typeof form.country;
                setForm(prev => ({ ...prev, country: newCountry }));
              }}
              className="w-full px-3 py-3 sm:py-2 text-base sm:text-sm text-white focus:border-rb-brand focus:ring-1 focus:ring-rb-brand/20 outline-none transition-all rb-inner-card-sm"
            >
              <option value="GB">🇬🇧 United Kingdom</option>
              <option value="AU">🇦🇺 Australia</option>
              <option value="US">🇺🇸 United States</option>
              <option value="CA">🇨🇦 Canada</option>
              <option value="NZ">🇳🇿 New Zealand</option>
              <option value="IE">🇮🇪 Ireland</option>
              <option value="DE">🇩🇪 Germany</option>
              <option value="FR">🇫🇷 France</option>
              <option value="SG">🇸🇬 Singapore</option>
              <option value="OTHER">🌍 Other</option>
            </select>
            <p className="text-[10px] text-slate-500">This determines which regulatory fields are shown</p>
          </div>
          {field('Company Name', Building2, 'companyName', isUK ? 'Reynolds & Bauhm' : 'Reynolds & Bauhm')}
          {isUK && field('Companies House No.', Hash, 'companyRegNumber', '12345678')}
          {isUK && field('VAT Number', FileText, 'vatNumber', 'GB123456789')}
          {isAU && field('ABN / ACN', BadgeCheck, 'abn', '12 345 678 901')}
          {isAU && field('EPA Licence No.', FileText, 'licenseNumber', 'WWT-2025-NSW-001')}
          {field('Job Title', Briefcase, 'jobTitle', 'Environmental Engineer')}
        </div>

        <div className="h-px bg-slate-800" />

        {/* Contact Section */}
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Contact Details
          </h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {field('Full Name', User, 'fullName', isUK ? 'John Reynolds' : 'John Bauhm')}
          {field('Email', Mail, 'email', 'john@reynoldsbauhm.com')}
          {field('Phone', Phone, 'phone', isUK ? '+44 1753 123456' : '+61 2 1234 5678')}
          {field('Address', MapPin, 'address', isUK ? 'Windsor, Berkshire, SL4 1LB' : 'Sydney, NSW 2000')}
        </div>
      </div>

      {/* Preview banner */}
      <div className="p-4 rounded-xl border border-slate-800/50 bg-slate-900/30">
        <p className="text-xs text-slate-400">
          <span className="text-rb-brand font-bold">Top Bar Preview:</span>{' '}
          {form.companyName || 'Your Company'} · {form.fullName || 'Your Name'} · {isUK ? '🇬🇧 UK EA' : isAU ? '🇦🇺 AU EPA' : 'Standard'}
        </p>
      </div>

      {/* Bottom Save — easy to reach after filling the form on mobile */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={handleSave}
        className="w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl text-base font-semibold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all shadow-lg shadow-[#cda42e]/15 sm:hidden"
      >
        <Save className="w-5 h-5" /> Save Profile
      </motion.button>
    </div>
  );
}
